import { Fragment, type ReactNode } from 'react';

import Link from '@shared/router/link';

/**
 *	Chat message markup.
 *
 *	A message is stored as typed, with two kinds of token in it:
 *
 *	- a mention, `@{userId}`, which reads as `@First Last`;
 *	- WhatsApp's formatting symbols, `*bold*`, `_italic_` and `~strikethrough~`.
 *
 *	`parseChatMarkup` splits a message into runs of text, mentions and links,
 *	each carrying its formatting, with the symbols dropped. `renderChatMarkup`
 *	turns those runs into inline elements for a chat bubble, and `toPlainText`
 *	into text for previews, quotes and copying.
 *
 *	The symbols follow WhatsApp's rules:
 *
 *	- a symbol opens a run only at a word boundary: not inside `snake_case`;
 *	- there is no space just inside a pair, so `* not bold*` stays as typed;
 *	- a symbol without a partner on the same line stays as typed;
 *	- pairs nest, so `*_both_*` is bold and italic;
 *	- nothing inside a link, an e-mail address or a mention is a symbol.
 *
 *	A link is found as GitHub finds one in a comment: `http://`, `https://`
 *	or `www.` up to the next space, less the punctuation that ends a sentence
 *	around it, and an e-mail address.
 *
 *	Who a mention names is not Playbook's to know. Every function that has to
 *	name one takes `nameOf`, which turns the id inside a token into a name, or
 *	`undefined` when there is nobody to name, in which case the token stays
 *	as typed. How a link opens is not Playbook's to know either: a router
 *	opens its own pages in place, so `renderChatMarkup` takes `renderLink`.
 */

export interface TextStyle {
	bold: boolean;
	italic: boolean;
	strikethrough: boolean;
}

export interface TextSegment extends TextStyle {
	type: 'text';
	text: string;
}

export interface MentionSegment extends TextStyle {
	type: 'mention';
	/** The id inside the token, as written: the digits of `@{123}`. */
	id: string;
	/** The token as stored, `@{123}`, for when the user can't be named. */
	token: string;
}

export interface LinkSegment extends TextStyle {
	type: 'link';
	/** The link as typed: `www.club.se`, `https://club.se/a` or `info@club.se`. */
	text: string;
	/** Where it goes: `https://` before a `www.` link, `mailto:` before an address. */
	href: string;
}

export type Segment = TextSegment | MentionSegment | LinkSegment;

/** The name behind a mention's id, without the `@`, or nobody. */
export type NameOf = (id: string) => string | undefined;

/**
 *	A link as an element: one that opens `href`, around its `text`. Keep it
 *	the same function from render to render, as `nameOf`.
 */
export type RenderLink = (href: string, text: string) => ReactNode;

const Markers: ReadonlyArray<[symbol: string, style: keyof TextStyle]> = [
	['*', 'bold'],
	['_', 'italic'],
	['~', 'strikethrough'],
];

/**
 *	How a mention is stored: `@{userId}`. Stateless on purpose: a shared `/g`
 *	regex carries `lastIndex` between callers, and one `.test()` elsewhere
 *	would make the next parse skip mentions.
 */
export const MentionToken: RegExp = /@\{(\d+)\}/;

function allMentionTokens(): RegExp {
	return new RegExp(MentionToken.source, 'g');
}

/**
 *	A web address, from its scheme or `www.`, up to the next space or `<`,
 *	as GitHub reads one. Its trailing punctuation is trimmed by `trimLink`.
 *
 *	It starts where a word could: not after a letter or a digit, so that
 *	`xhttps://` is none, but after `_`, so `_www.club.se_` is an italic
 *	link. That is the character before it, matched as the first group: a
 *	lookbehind would say it shorter, and fails to parse in the WebKit of an
 *	iOS before 16.4. Used only through `matchAll`, which never moves its
 *	`lastIndex`.
 */
const WebAddresses: RegExp =
	/(^|[^\p{L}\p{N}])((?:(?:https?|ftp):\/\/|www\.)[^\s<]+)/giu;

/** The scheme, or `www.`, a web address starts with. */
const WebAddressStart: RegExp = /^(?:(?:https?|ftp):\/\/|www\.)/i;

/**
 *	An e-mail address, in any script, so `märta@club.se` is all one, and
 *	starting where its run of letters does, which the first group holds as
 *	`WebAddresses` holds its own. Without that start, a long run of letters
 *	is read again from every one of them, which took over a second for a
 *	50,000-letter paste. Used only through `matchAll`, as `WebAddresses`.
 */
const EmailAddresses: RegExp =
	/(^|[^\p{L}\p{N}._+-])([\p{L}\p{N}._+-]+@(?:[\p{L}\p{N}-]+\.)+\p{L}{2,})/giu;

/**
 *	What ends a link's sentence rather than the link: GitHub's trailing
 *	punctuation, and quotes, `;` and `>` for an address someone quoted.
 */
const TrailingPunctuation: string = '?!.,:;*_~\'">' as const;

/** Each closing bracket, by the opening one it pairs with. */
const Brackets: ReadonlyMap<string, string> = new Map([
	[')', '('],
	[']', '['],
	['}', '{'],
]);

const BracketCharacters: string = '()[]{}' as const;

/**
 *	A web address less the punctuation around it: `see https://x.com.`
 *	does not link the full stop, and `(https://x.com)` not the `)`. A
 *	closing bracket goes only while the address has more of it than of its
 *	opening one, so `https://x.com/a_(b)` keeps its own. Linear: each
 *	bracket is counted once and dropped from the count as it goes.
 */
function trimLink(address: string): string {
	const counts = new Map<string, number>();

	for (const character of address) {
		if (BracketCharacters.includes(character)) {
			counts.set(character, (counts.get(character) ?? 0) + 1);
		}
	}

	let end: number = address.length;

	while (end > 0) {
		const last: string = address[end - 1] ?? '';
		const opening: string | undefined = Brackets.get(last);

		const trailing: boolean = TrailingPunctuation.includes(last);
		const unpaired: boolean =
			!!opening && (counts.get(last) ?? 0) > (counts.get(opening) ?? 0);

		if (!trailing && !unpaired) {
			break;
		}

		if (opening) {
			counts.set(last, (counts.get(last) ?? 0) - 1);
		}

		end--;
	}

	return address.slice(0, end);
}

/**
 *	A stretch of the message that is never formatted, as WhatsApp leaves
 *	it, so underscores in a URL path are not italics: a link, a mention's
 *	token, or an `ftp://` address, which is no link, since no browser opens
 *	one any more.
 */
type Span = { start: number; text: string } & (
	| { kind: 'link'; href: string }
	| { kind: 'mention'; id: string }
	| { kind: 'opaque' }
);

/**
 *	Every span in the message, in order. Where two overlap, the one that
 *	starts first wins, and the longer of two that start together, so an
 *	address inside a URL's path is part of the URL.
 */
function spansOf(content: string): Span[] {
	const found: Span[] = [];

	for (const match of content.matchAll(WebAddresses)) {
		const [, before = '', address = ''] = match;
		const start: number = match.index + before.length;
		const text: string = trimLink(address);
		const prefix: string = WebAddressStart.exec(text)?.[0] ?? '';

		// Trimmed down to its scheme, or less: `https://.` or `www..`.
		if (!prefix || text.length <= prefix.length) {
			continue;
		}

		if (prefix.toLowerCase() === 'ftp://') {
			found.push({ kind: 'opaque', start, text });
		} else {
			const href: string =
				prefix.toLowerCase() === 'www.' ? `https://${text}` : text;

			found.push({ kind: 'link', start, text, href });
		}
	}

	for (const match of content.matchAll(EmailAddresses)) {
		const [, before = '', address = ''] = match;

		found.push({
			kind: 'link',
			start: match.index + before.length,
			text: address,
			href: `mailto:${address}`,
		});
	}

	for (const match of content.matchAll(allMentionTokens())) {
		found.push({
			kind: 'mention',
			start: match.index,
			text: match[0],
			id: match[1] ?? '',
		});
	}

	found.sort((a, b) => a.start - b.start || b.text.length - a.text.length);

	const spans: Span[] = [];

	let end: number = 0;

	for (const span of found) {
		if (span.start >= end) {
			spans.push(span);
			end = span.start + span.text.length;
		}
	}

	return spans;
}

/**
 *	The text as the symbol scan reads it: every span replaced by letters of
 *	the same length, so nothing inside it is a symbol and, like any other
 *	word, a symbol touching it does not open or close a run.
 */
function maskSpans(content: string, spans: Span[]): string {
	let masked: string = '';
	let index: number = 0;

	for (const span of spans) {
		masked += content.slice(index, span.start) + 'x'.repeat(span.text.length);
		index = span.start + span.text.length;
	}

	return masked + content.slice(index);
}

function isWordCharacter(character: string | undefined): boolean {
	return character !== undefined && /[\p{L}\p{N}]/u.test(character);
}

function isWhitespace(character: string | undefined): boolean {
	return character !== undefined && /\s/.test(character);
}

/** Whether the symbol at `index` can open a run. */
function opensRun(text: string, index: number, symbol: string): boolean {
	const before: string | undefined = text[index - 1];
	const after: string | undefined = text[index + 1];

	return (
		!isWordCharacter(before) &&
		after !== undefined &&
		after !== symbol &&
		!isWhitespace(after)
	);
}

/**
 *	The index of the symbol that closes the run opened at `start`, or -1. The
 *	run holds at least one character, ends in a non-space, ends at a word
 *	boundary and never crosses a line break.
 */
function findClosing(text: string, start: number, symbol: string): number {
	for (let index = start + 2; index < text.length; index++) {
		const character: string | undefined = text[index];

		if (character === '\n') {
			return -1;
		}

		if (
			character === symbol &&
			!isWhitespace(text[index - 1]) &&
			!isWordCharacter(text[index + 1])
		) {
			return index;
		}
	}

	return -1;
}

/**
 *	Marks every run of one symbol: the symbols themselves in `symbols`, and
 *	`bit` on every character between them in `styles`.
 *
 *	Linear in the text: a closer for an opener further along the line would
 *	have closed this one too, so an opener that finds none ends the line for
 *	every opener after it.
 */
function markRuns(
	text: string,
	symbol: string,
	bit: number,
	styles: Uint8Array,
	symbols: Uint8Array
): void {
	let index: number = 0;

	while (index < text.length) {
		if (text[index] !== symbol || !opensRun(text, index, symbol)) {
			index++;
			continue;
		}

		const end: number = findClosing(text, index, symbol);

		if (end === -1) {
			const lineEnd: number = text.indexOf('\n', index);

			index = lineEnd === -1 ? text.length : lineEnd + 1;
			continue;
		}

		symbols[index] = 1;
		symbols[end] = 1;

		for (let inside = index + 1; inside < end; inside++) {
			styles[inside] = (styles[inside] ?? 0) | bit;
		}

		index = end + 1;
	}
}

/** The style a character's bits in a `MarkupScan` stand for. */
export function styleOf(bits: number): TextStyle {
	return {
		bold: (bits & 1) !== 0,
		italic: (bits & 2) !== 0,
		strikethrough: (bits & 4) !== 0,
	};
}

export interface MarkupScan {
	/** Per character, the style bits `styleOf` reads: 1 bold, 2 italic, 4 strikethrough. */
	styles: Uint8Array;
	/** Per character, whether it is one of the symbols. */
	symbols: Uint8Array;
}

/**
 *	Marks every character of a message: its formatting, and whether it is a
 *	symbol. The field uses this while a message is typed, since it keeps the
 *	symbols visible and dims them; `parseChatMarkup` uses it to drop them.
 *	Links, e-mail addresses and mention tokens are masked first, so nothing
 *	inside them is a symbol and a symbol touching one does not open a run.
 */
export function scanChatMarkup(content: string): MarkupScan {
	return scanAround(content, spansOf(content));
}

function scanAround(content: string, spans: Span[]): MarkupScan {
	const styles = new Uint8Array(content.length);
	const symbols = new Uint8Array(content.length);
	const scan: string = maskSpans(content, spans);

	Markers.forEach(([symbol], bit) =>
		markRuns(scan, symbol, 1 << bit, styles, symbols)
	);

	return { styles, symbols };
}

/**
 *	Splits a message into runs of text, mentions and links, each with its
 *	formatting. The formatting symbols are dropped; line breaks stay in the
 *	text.
 */
export function parseChatMarkup(content: string): Segment[] {
	const spans: Span[] = spansOf(content);
	const { styles, symbols } = scanAround(content, spans);

	/** The mentions and links, by where they start. */
	const starting = new Map<number, Span>();

	for (const span of spans) {
		if (span.kind !== 'opaque') {
			starting.set(span.start, span);
		}
	}

	const segments: Segment[] = [];

	let text: string = '';
	let style: number = 0;

	const flush = (): void => {
		if (text.length > 0) {
			segments.push({ type: 'text', text, ...styleOf(style) });
			text = '';
		}
	};

	let index: number = 0;

	while (index < content.length) {
		const span: Span | undefined = starting.get(index);

		if (span) {
			flush();

			// Nothing inside a span is a symbol, so it is all one style.
			const spanStyle: TextStyle = styleOf(styles[index] ?? 0);

			if (span.kind === 'mention') {
				segments.push({
					type: 'mention',
					id: span.id,
					token: span.text,
					...spanStyle,
				});
			} else if (span.kind === 'link') {
				segments.push({
					type: 'link',
					text: span.text,
					href: span.href,
					...spanStyle,
				});
			}

			index += span.text.length;
			continue;
		}

		if (symbols[index] === 1) {
			index++;
			continue;
		}

		if (styles[index] !== style) {
			flush();
			style = styles[index] ?? 0;
		}

		text += content[index];
		index++;
	}

	flush();

	return segments;
}

/** How a mention reads: `@First Last`, or the token when there is nobody. */
function mentionText(segment: MentionSegment, nameOf: NameOf): string {
	const name: string = nameOf(segment.id)?.trim() ?? '';

	return name.length > 0 ? `@${name}` : segment.token;
}

interface PlainTextOptions {
	/**
	 *	Drop the formatting symbols. Only for a surface whose bubbles render
	 *	the formatting: where they show `*great*` as typed, a preview or quote
	 *	that reads `great` disagrees with the message, and `~no~` loses its
	 *	meaning. Copy always keeps them, so pasting back keeps the formatting.
	 */
	dropSymbols?: boolean;
}

/**
 *	The message as text: mentions as names, a token that matches nobody left
 *	as typed, and the formatting symbols kept unless asked to drop them.
 */
export function toPlainText(
	content: string,
	nameOf: NameOf,
	{ dropSymbols = false }: PlainTextOptions = {}
): string {
	if (!content) {
		return content;
	}

	if (!dropSymbols) {
		return content.replace(allMentionTokens(), (token: string, id: string) =>
			mentionText({ type: 'mention', id, token, ...styleOf(0) }, nameOf)
		);
	}

	return parseChatMarkup(content)
		.map((segment) =>
			segment.type === 'mention' ? mentionText(segment, nameOf) : segment.text
		)
		.join('');
}

/** `text` inside the inline elements its formatting calls for. */
function styled(text: ReactNode, style: TextStyle): ReactNode {
	let node: ReactNode = text;

	if (style.strikethrough) {
		node = <s>{node}</s>;
	}

	if (style.italic) {
		node = <em>{node}</em>;
	}

	if (style.bold) {
		node = <strong>{node}</strong>;
	}

	return node;
}

/**
 *	A link as Playbook draws one when it is given no `renderLink`: a web
 *	address through the shared router's `Link`, which opens a page on this
 *	site in place and any other in a new tab.
 *
 *	An e-mail address is a bare anchor, the one place a link here is not
 *	the router's: `Link` takes `mailto:`, which has no host, for a path on
 *	this site, and would push it onto the history. A bare anchor hands it
 *	to the mail app, without leaving an empty tab behind.
 */
export const DefaultLink: RenderLink = (
	href: string,
	text: string
): ReactNode =>
	href.startsWith('mailto:') ? (
		<a href={href}>{text}</a>
	) : (
		<Link href={href}>{text}</Link>
	);

interface RenderOptions {
	/** Draws each link. Defaults to `DefaultLink`. */
	renderLink?: RenderLink;
}

/**
 *	The message as inline content for `atoms/chat-bubble`: `<strong>`, `<em>`
 *	and `<s>` for the formatting, a mention as `<strong>` carrying the user's
 *	id in `data-mention`, which is how Figma draws one: bold in the bubble's
 *	own text colour, with no chip, and a link as `renderLink` draws it. A
 *	token nobody can name stays as typed. Line breaks stay in the text for
 *	the bubble's `pre-wrap`.
 */
export function renderChatMarkup(
	content: string,
	nameOf: NameOf,
	{ renderLink = DefaultLink }: RenderOptions = {}
): ReactNode {
	return (
		<Fragment>
			{parseChatMarkup(content).map((segment, index) => {
				if (segment.type === 'text') {
					return (
						<Fragment key={index}>{styled(segment.text, segment)}</Fragment>
					);
				}

				if (segment.type === 'link') {
					return (
						<Fragment key={index}>
							{styled(renderLink(segment.href, segment.text), segment)}
						</Fragment>
					);
				}

				const name: string | undefined = nameOf(segment.id)?.trim();

				if (!name) {
					return (
						<Fragment key={index}>{styled(segment.token, segment)}</Fragment>
					);
				}

				return (
					<Fragment key={index}>
						{styled(
							<strong data-mention={segment.id}>@{name}</strong>,
							segment
						)}
					</Fragment>
				);
			})}
		</Fragment>
	);
}
